import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MessageCircle } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { SITE } from "@/data/site";
import { useLanguage } from "@/i18n/LanguageContext";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden whitespace-nowrap pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLanguage();
  const [introDone, setIntroDone] = useState(false);
  const { scrollY } = useScroll();
  const yVisual = useTransform(scrollY, [0, 900], [0, 170]);
  const yHeadline = useTransform(scrollY, [0, 900], [0, -70]);
  const lines = t("hero.lines");

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {!introDone && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#050505]"
          initial={{ y: 0 }}
          animate={{ y: "-100%" }}
          transition={{ duration: 1, ease: EASE, delay: 0.55 }}
          onAnimationComplete={() => setIntroDone(true)}
          data-testid="hero-intro-overlay"
        >
          <motion.img
            src="/assets/logo-white.png"
            alt="Payung Oren Creative"
            className="h-14 w-auto md:h-16"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          />
        </motion.div>
      )}

      <div
        className="pointer-events-none absolute -right-48 top-1/4 h-[560px] w-[560px] rounded-full bg-[#F97316]/15 blur-[180px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid min-h-screen max-w-[1440px] grid-cols-1 items-end gap-12 px-6 pb-10 pt-32 md:px-12 lg:pb-16">
        <div className="pointer-events-none absolute right-6 top-28 z-0 hidden w-[24vw] max-w-[360px] md:right-12 lg:block">
          <motion.div
            style={{ y: yVisual }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 1.1, ease: EASE }}
            className="relative"
          >
            <div className="absolute -left-6 -top-6 h-full w-full rounded-2xl border border-[#F97316]/30" aria-hidden="true" />
            <div className="aspect-[3/4] overflow-hidden rounded-2xl border border-white/10">
              <img
                src="/assets/team-control.jpg"
                alt={t("hero.visualAlt")}
                className="h-full w-full object-cover"
                data-testid="hero-visual"
              />
            </div>
          </motion.div>
        </div>

        <motion.div className="relative z-10" style={{ y: yHeadline }}>
          <motion.p
            className="mb-8 flex items-center gap-4 font-spacemono text-[11px] uppercase tracking-[0.35em] text-white/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7, duration: 0.8 }}
          >
            <span className="h-px w-10 bg-[#F97316]" />
            {t("hero.kicker")}
          </motion.p>

          <h1 className="font-syne text-[7.4vw] font-extrabold uppercase leading-[0.96] tracking-tighter sm:text-[7vw] lg:text-[5.5vw]" data-testid="hero-headline">
            <RevealLine delay={1.15}>{lines[0]}</RevealLine>
            <RevealLine delay={1.27}>
              <span className="text-outline">{lines[1]}</span>
            </RevealLine>
            <RevealLine delay={1.39}>{lines[2]}</RevealLine>
            <RevealLine delay={1.51}>
              <span className="text-[#F97316]">{lines[3]}</span>
            </RevealLine>
          </h1>

          <motion.p
            className="mt-10 max-w-md text-sm leading-relaxed text-white/60 md:text-base"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.95, duration: 0.9, ease: EASE }}
            data-testid="hero-intro"
          >
            {t("hero.intro")}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.1, duration: 0.9, ease: EASE }}
          >
            <a
              href={`mailto:${SITE.email}?subject=${encodeURIComponent("Proposal request — Payung Oren Creative")}`}
              data-testid="hero-cta-proposal"
              className="group flex items-center gap-3 rounded-full bg-[#F97316] px-8 py-4 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#EA580C] active:scale-[0.96]"
            >
              {t("hero.ctaProposal")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href={`https://wa.me/${SITE.whatsappCorporate}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="group flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white/80 transition-[border-color,color,transform] duration-300 hover:border-[#F97316] hover:text-white active:scale-[0.96]"
            >
              <MessageCircle className="h-4 w-4" />
              {t("hero.ctaDiscuss")}
            </a>
            <a
              href="#work"
              data-testid="hero-cta-portfolio"
              className="group flex items-center gap-2 px-2 py-4 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-[#F97316]"
            >
              {t("hero.ctaPortfolio")}
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.35, duration: 0.9 }}
            data-testid="hero-trust-badges"
          >
            {t("hero.badges").map((b) => (
              <span
                key={b}
                className="flex items-center gap-3 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/45"
              >
                <span className="h-1 w-1 bg-[#F97316]" />
                {b}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="mx-auto flex max-w-[1440px] items-center justify-between border-t border-white/10 px-6 py-5 md:px-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.9 }}
      >
        <span className="flex items-center gap-3 font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">
          {t("hero.scroll")}
          <span className="relative h-8 w-px overflow-hidden bg-white/15">
            <span className="animate-scroll-pulse absolute inset-x-0 h-full bg-[#F97316]" />
          </span>
          <ArrowDown className="h-3 w-3 text-[#F97316]" />
        </span>
        <span className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{t("hero.tagline")}</span>
      </motion.div>
    </section>
  );
}
